import React, { useState, useRef } from "react";
import { Plus, Download, Pencil, Trash2, Upload, Loader2 } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useData } from "@/context/DataContext";
import { PageHeader, EventSelector, KpiCard, ProgressBar, NoEvent } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { euro, formatDate, progressBarClass, CHART_COLORS } from "@/lib/format";
import { uploadFile } from "@/lib/upload";
import { Wallet, TrendingDown, PiggyBank } from "lucide-react";
import client from "@/lib/api";
import { toast } from "sonner";
import { EXPENSE_TYPES } from "@/lib/eventConstants";

const TYPES = EXPENSE_TYPES;
const PAYMENTS = ["Carte bancaire", "Virement", "Chèque", "Espèces", "Prélèvement"];
const EMPTY = { type: "", description: "", amount: "", date: "", paymentMethod: "", justificatif: "", invoiceUrl: "", acompte: "" };

export default function Budget() {
  const { selectedEvent, expensesByEvent, addExpense, updateExpense, deleteExpense } = useData();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [importing, setImporting] = useState(false);
  const invoiceRef = useRef(null);

  const onInvoice = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      // 1) Upload the invoice file so it's saved with the expense
      let invoiceUrl = "";
      try { invoiceUrl = await uploadFile(file); } catch { /* store local ref only */ }
      // 2) OCR via IA
      const fd = new FormData(); fd.append("file", file);
      const { data } = await client.post("/ai/parse-invoice", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ type: data.type || "", description: data.description || "", amount: data.amount || "",
                acompte: data.acompte || "", date: data.date || "", paymentMethod: data.paymentMethod || "",
                justificatif: data.justificatif || file.name, invoiceUrl });
      setEditId(null); setShowForm(true);
      const acc = Number(data.acompte || 0);
      toast.success(acc > 0
        ? `Facture lue par l'IA — total TTC ${Number(data.amount||0)}€ (acompte ${acc}€ pris en compte)`
        : "Facture lue par l'IA — vérifiez et validez les champs");
    } catch { toast.error("Lecture de la facture impossible"); } finally { setImporting(false); ev.target.value = ""; }
  };

  if (!selectedEvent) return (<><PageHeader title="Suivi du budget" /><NoEvent /></>);

  const expenses = expensesByEvent(selectedEvent.id);
  const spent = expenses.reduce((a, x) => a + Number(x.amount || 0), 0);
  const remaining = (selectedEvent.budget || 0) - spent;
  const usage = selectedEvent.budget ? Math.round((spent / selectedEvent.budget) * 100) : 0;

  const byCat = {};
  expenses.forEach((x) => { byCat[x.type || "Divers"] = (byCat[x.type || "Divers"] || 0) + Number(x.amount || 0); });
  const pieData = Object.entries(byCat).map(([name, value]) => ({ name, value }));

  const onJustif = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const url = await uploadFile(file); setForm((p) => ({ ...p, justificatif: url })); toast.success("Justificatif ajouté"); } catch { toast.error("Échec upload"); }
  };

  const submit = async () => {
    if (!form.type || !form.amount) { toast.error("Type et montant requis"); return; }
    const payload = { ...form, amount: Number(form.amount), eventId: selectedEvent.id };
    if (editId) await updateExpense(editId, payload); else await addExpense(payload);
    toast.success("Dépense enregistrée");
    setShowForm(false); setForm(EMPTY); setEditId(null);
  };

  const exportCsv = () => {
    const rows = [["Type", "Description", "Montant", "Date", "Paiement"], ...expenses.map((x) => [x.type, x.description, x.amount, x.date, x.paymentMethod])];
    const csv = rows.map((r) => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `budget-${selectedEvent.name}.csv`; a.click();
  };

  return (
    <>
      <PageHeader title="Suivi du budget" actions={
        <div className="flex gap-2 flex-wrap"><EventSelector />
          <input ref={invoiceRef} type="file" accept="image/*,application/pdf,.pdf" className="hidden" onChange={onInvoice} data-testid="invoice-input" />
          <Button variant="outline" onClick={() => invoiceRef.current?.click()} disabled={importing} data-testid="import-invoice">{importing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}Importer une facture (IA)</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="export-budget"><Download className="h-4 w-4 mr-1" />Exporter</Button>
          <Button onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(true); }} data-testid="add-expense-button"><Plus className="h-4 w-4 mr-1" />Ajouter une dépense</Button>
        </div>} />



      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <KpiCard icon={Wallet} tint="blue" label="Budget total" value={euro(selectedEvent.budget)} />
        <KpiCard icon={TrendingDown} tint="amber" label="Dépensé" value={euro(spent)} sub={`${usage}% du budget`} />
        <div className="bg-card rounded-xl border border-border p-5">
          <div className="flex items-center justify-between mb-2"><span className="text-xs font-medium text-muted-foreground uppercase">Restant</span><PiggyBank className="h-5 w-5 text-emerald-600" /></div>
          <p className={`font-display text-2xl font-bold ${remaining < 0 ? "text-rose-600" : ""}`}>{euro(remaining)}</p>
          <ProgressBar pct={usage} className="mt-3" />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="font-display font-semibold mb-4">Répartition par catégorie</h3>
          {pieData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart><Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Pie><Tooltip formatter={(v) => euro(v)} /><Legend /></PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-muted-foreground">Aucune dépense.</p>}
        </div>
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="font-display font-semibold mb-4">Détail des catégories</h3>
          <div className="space-y-3">
            {pieData.length ? pieData.map((c, i) => (
              <div key={c.name} className="flex items-center gap-3">
                <span className="h-3 w-3 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                <span className="flex-1 text-sm font-medium">{c.name}</span>
                <span className="text-sm">{euro(c.value)}</span>
                <span className="text-xs text-muted-foreground w-12 text-right">{spent ? Math.round((c.value / spent) * 100) : 0}%</span>
              </div>
            )) : <p className="text-sm text-muted-foreground">Aucune donnée.</p>}
          </div>
        </div>
      </div>

      {showForm && (
        <div className="bg-card rounded-xl border border-border p-6 mb-6">
          <h3 className="font-display font-semibold mb-4">Ajouter une dépense</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="mb-1.5 block">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger data-testid="expense-type"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="mb-1.5 block">Montant (€)</Label><Input type="number" data-testid="expense-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block">Description</Label><Input data-testid="expense-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Date</Label><Input type="date" data-testid="expense-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Mode de paiement</Label>
              <Select value={form.paymentMethod} onValueChange={(v) => setForm({ ...form, paymentMethod: v })}><SelectTrigger data-testid="expense-payment"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{PAYMENTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block">Justificatif</Label><Input type="file" onChange={onJustif} data-testid="expense-justif" /></div>
          </div>
          <div className="flex justify-end gap-2 mt-4"><Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button><Button onClick={submit} data-testid="save-expense">Enregistrer</Button></div>
        </div>
      )}

      <div className="bg-card rounded-xl border border-border p-5">
        <h3 className="font-display font-semibold mb-4">Dépenses directes</h3>
        <Table>
          <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Description</TableHead><TableHead>Montant</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {expenses.length ? expenses.map((x) => (
              <TableRow key={x.id} data-testid={`expense-${x.id}`}>
                <TableCell className="font-medium">{x.type}</TableCell>
                <TableCell className="text-muted-foreground">{x.description}</TableCell>
                <TableCell>{euro(x.amount)}</TableCell>
                <TableCell>{formatDate(x.date)}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => { setForm(x); setEditId(x.id); setShowForm(true); }} data-testid={`edit-expense-${x.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => deleteExpense(x.id)} data-testid={`delete-expense-${x.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            )) : <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Aucune dépense enregistrée.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
